import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useConvexAuth } from 'convex/react'
import { Loader2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { OAuthCard } from '@/components/oauth-card'
import { authClient } from '@/lib/auth-client'
import { stashAuthorizeRequest, takeAuthorizeRequest } from '@/lib/oauth-flow'
import { stashReturnTo } from '@/lib/return-to'

// better-auth's oidc-provider sends signed-out MCP clients here (its loginPage).
// The session lives in this app, not on the auth host, so once signed in we
// replay the authorize request with it and follow where it points: the client's
// callback, or the consent page.
export const Route = createFileRoute('/oauth/authorize')({
  head: () => ({ meta: [{ title: 'Connect — Goaltree' }] }),
  component: AuthorizePage,
})

function AuthorizePage() {
  const { t } = useTranslation()
  const { isAuthenticated, isLoading } = useConvexAuth()
  const navigate = useNavigate()
  const [failed, setFailed] = useState(false)
  const started = useRef(false)

  useEffect(() => {
    if (window.location.search.includes('client_id=')) stashAuthorizeRequest(window.location.search)
  }, [])

  useEffect(() => {
    if (isLoading || started.current) return
    if (!isAuthenticated) {
      stashReturnTo('/oauth/authorize')
      navigate({ to: '/auth/login', search: { returnTo: '/oauth/authorize' }, replace: true })
      return
    }
    started.current = true
    const query = takeAuthorizeRequest()
    if (!query) {
      setFailed(true)
      return
    }
    authClient
      .$fetch<{ url?: string }>('/oauth2/authorize', { method: 'GET', query })
      .then(({ data }) => {
        if (data?.url) window.location.replace(data.url)
        else setFailed(true)
      })
      .catch(() => setFailed(true))
  }, [isAuthenticated, isLoading, navigate])

  return (
    <OAuthCard>
      {failed ? (
        <p className="text-sm text-muted-foreground">{t('oauth.error')}</p>
      ) : (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t('oauth.working')}
        </div>
      )}
    </OAuthCard>
  )
}
